import { useEffect, useId, useRef, useState } from 'react';
import { CalendarDaysIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import { PERIOD_OPTIONS } from './usuariosOnline.constants';

/**
 * Filtro compacto estilo barra: botón con icono + label del valor activo + menú de opciones.
 * Sin buscador; selección inmediata al hacer clic.
 */
export const CompactFilterMenu = ({
    label,
    icon: Icon,
    options = [],
    value,
    onChange,
    disabled = false,
    className = '',
}) => {
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);
    const listId = useId();
    const selected = options.find((option) => option.value === value) || options[0];

    useEffect(() => {
        if (!open) return undefined;
        const onPointerDown = (event) => {
            if (!rootRef.current?.contains(event.target)) setOpen(false);
        };
        const onKeyDown = (event) => {
            if (event.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open]);

    useEffect(() => {
        if (disabled) setOpen(false);
    }, [disabled]);

    return (
        <div ref={rootRef} className={`relative inline-flex ${className}`.trim()}>
            <button
                type='button'
                disabled={disabled}
                aria-haspopup='listbox'
                aria-expanded={open}
                aria-controls={listId}
                aria-label={label ? `${label}: ${selected?.label || ''}` : selected?.label}
                onClick={() => setOpen((prev) => !prev)}
                className='inline-flex max-w-full items-center gap-1.5 rounded-md bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300/80 transition hover:bg-gray-50 hover:text-gray-900 hover:shadow-md hover:ring-gray-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400 disabled:shadow-none disabled:ring-gray-200'
            >
                {Icon ? <Icon className='size-3.5 shrink-0 text-slate-500' aria-hidden='true' /> : null}
                <span className='truncate'>{selected?.label || 'Filtrar'}</span>
                <ChevronDownIcon
                    className={`size-3.5 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
                    aria-hidden='true'
                />
            </button>

            {open ? (
                <ul
                    id={listId}
                    role='listbox'
                    aria-label={label || 'Opciones de filtro'}
                    className='absolute right-0 z-30 mt-1 min-w-[11rem] overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-lg'
                >
                    {options.map((option) => {
                        const isActive = option.value === value;
                        return (
                            <li key={option.value} role='option' aria-selected={isActive}>
                                <button
                                    type='button'
                                    className={`flex w-full items-center px-3 py-2 text-left text-sm transition-colors ${
                                        isActive
                                            ? 'bg-indigo-50 font-semibold text-indigo-700'
                                            : 'text-slate-700 hover:bg-slate-50'
                                    }`}
                                    onClick={() => {
                                        onChange?.(option.value);
                                        setOpen(false);
                                    }}
                                >
                                    {option.label}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            ) : null}
        </div>
    );
};

export const PeriodCompactFilter = ({ value, onChange, disabled = false }) => (
    <CompactFilterMenu
        label='Periodo'
        icon={CalendarDaysIcon}
        options={PERIOD_OPTIONS}
        value={value}
        onChange={onChange}
        disabled={disabled}
    />
);
